'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  Bug,
  Lightbulb,
  CreditCard,
  User,
  BookOpen,
  Star,
  Send,
  Loader,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Plus,
  Mail,
  Phone,
  FileText,
  Trash2,
} from 'lucide-react';
import api from '../../services/api';

// ============================================================================
// API
// ============================================================================

const helpAPI = {
  getRequests: async (params?: { status?: string; category?: string }) => {
    const response = await api.get('/help', { params: { limit: 50, ...params } });
    return (response.data || []).map((r: any) => ({
      ...r,
      id: r.id || r._id,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
      resolvedAt: r.resolved_at,
      preferredContact: r.preferred_contact,
      browserInfo: r.browser_info,
      pageUrl: r.page_url,
      adminNotes: r.admin_notes,
    }));
  },
  createRequest: async (data: any) => {
    const response = await api.post('/help', {
      category: data.category,
      subject: data.subject,
      message: data.message,
      priority: data.priority,
      email: data.email,
      name: data.name,
      phone: data.phone || undefined,
      preferred_contact: data.preferredContact,
      browser_info: navigator.userAgent,
      page_url: window.location.href,
    });
    return response.data;
  },
  updateRequest: async (id: string, data: any) => {
    const response = await api.put(`/help/${id}`, data);
    return response.data;
  },
  deleteRequest: async (id: string) => {
    const response = await api.delete(`/help/${id}`);
    return response.data;
  },
};

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORIES = [
  { value: 'bug_report', label: 'Bug Report', icon: Bug, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', desc: 'Something isn\'t working correctly' },
  { value: 'feature_request', label: 'Feature Request', icon: Lightbulb, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', desc: 'Suggest a new feature or improvement' },
  { value: 'account_issue', label: 'Account Issue', icon: User, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', desc: 'Login, profile, or account problems' },
  { value: 'billing', label: 'Billing & Plans', icon: CreditCard, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', desc: 'Payments, invoices, or plan changes' },
  { value: 'how_to', label: 'How To / Guide', icon: BookOpen, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', desc: 'Need help using a feature' },
  { value: 'feedback', label: 'General Feedback', icon: Star, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', desc: 'Share your thoughts about PlugD' },
  { value: 'other', label: 'Other', icon: MessageSquare, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', desc: 'Anything else' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
];

const STATUS_CONFIG: Record<string, { label: string; icon: any; color: string; bg: string }> = {
  open: { label: 'Open', icon: AlertCircle, color: 'text-blue-700', bg: 'bg-blue-100' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'text-yellow-700', bg: 'bg-yellow-100' },
  resolved: { label: 'Resolved', icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-100' },
  closed: { label: 'Closed', icon: XCircle, color: 'text-gray-700', bg: 'bg-gray-100' },
};

const FAQ_ITEMS = [
  { q: 'How do I connect a social media account?', a: 'Go to Settings > Social Accounts and click "Connect Account". Follow the OAuth flow for the platform you want to connect.' },
  { q: 'How does AI Repurpose work?', a: 'Paste your content in the AI Repurpose tab under Content Intelligence. Select target platforms and click Repurpose. AI will adapt your content for each platform\'s format and character limits.' },
  { q: 'Can I schedule posts across multiple platforms?', a: 'Yes! Use the Composer to create your post, select multiple platforms, pick a date and time, then click Schedule. You can also use Bulk Schedule for mass uploads.' },
  { q: 'How do I set up a Brand Voice?', a: 'Go to Content Intelligence > Brand Voice tab. Click "Create New Brand Voice" and paste 2+ samples of your content. AI will analyze your writing style and create a voice profile.' },
  { q: 'What is the Post Queue?', a: 'The Post Queue lets you set time slots for each day of the week. When you add posts to the queue, they\'re automatically scheduled to your next available slot.' },
  { q: 'How do I track link clicks?', a: 'Use the Short Links feature under Tools. Create a short link and share it. Click analytics are tracked automatically and visible in the link dashboard.' },
];

// ============================================================================
// COMPONENT
// ============================================================================

interface HelpFormData {
  category: string;
  subject: string;
  message: string;
  priority: string;
  email: string;
  name: string;
  phone: string;
  preferredContact: string;
}

const emptyForm: HelpFormData = {
  category: '',
  subject: '',
  message: '',
  priority: 'medium',
  email: '',
  name: '',
  phone: '',
  preferredContact: 'email',
};

export default function HelpCenterPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeView, setActiveView] = useState<'faq' | 'submit' | 'history'>('faq');
  const [form, setForm] = useState<HelpFormData>(emptyForm);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [followUpText, setFollowUpText] = useState('');

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['helpRequests', statusFilter],
    queryFn: () => helpAPI.getRequests(statusFilter ? { status: statusFilter } : undefined),
    enabled: activeView === 'history',
  });

  const createMutation = useMutation({
    mutationFn: helpAPI.createRequest,
    onSuccess: () => {
      toast.success(t('help_submitted_success', 'Your request has been submitted! We\'ll get back to you soon.'));
      queryClient.invalidateQueries({ queryKey: ['helpRequests'] });
      setForm(emptyForm);
      setActiveView('history');
    },
    onError: () => toast.error(t('help_submit_failed', 'Failed to submit request. Please try again.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => helpAPI.updateRequest(id, data),
    onSuccess: () => {
      toast.success(t('help_request_updated', 'Request updated'));
      queryClient.invalidateQueries({ queryKey: ['helpRequests'] });
      setFollowUpText('');
    },
    onError: () => toast.error(t('help_update_failed', 'Failed to update request')),
  });

  const deleteMutation = useMutation({
    mutationFn: helpAPI.deleteRequest,
    onSuccess: () => {
      toast.success(t('help_request_deleted', 'Request deleted'));
      queryClient.invalidateQueries({ queryKey: ['helpRequests'] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.category || !form.subject || !form.message || !form.email || !form.name) {
      toast.error(t('help_fill_required', 'Please fill in all required fields'));
      return;
    }
    createMutation.mutate(form);
  };

  return (
    <div className="bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8">
          {[
            { key: 'faq', label: t('help_faq_tab', 'FAQ'), icon: BookOpen },
            { key: 'submit', label: t('help_submit_request_tab', 'Submit Request'), icon: Plus },
            { key: 'history', label: t('help_my_requests_tab', 'My Requests'), icon: FileText },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveView(tab.key as any)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                activeView === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* FAQ Section */}
        {activeView === 'faq' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-5"
          >
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100 bg-white">
                <h2 className="text-lg font-bold tracking-tight text-gray-900 flex items-center gap-2">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                  </div>
                  {t('help_faq_heading', 'Frequently Asked Questions')}
                </h2>
                <p className="text-sm text-gray-500 mt-2">{t('help_faq_subheading', 'Quick answers to common questions')}</p>
              </div>
              <div className="divide-y divide-gray-100">
                {FAQ_ITEMS.map((item, idx) => (
                  <div key={idx}>
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors duration-200"
                    >
                      <span className="text-sm font-medium text-gray-800">{item.q}</span>
                      {expandedFaq === idx ? (
                        <ChevronUp className="w-4 h-4 text-emerald-600 flex-shrink-0 ml-2 transition-transform" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform" />
                      )}
                    </button>
                    <AnimatePresence>
                      {expandedFaq === idx && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <p className="px-6 pb-4 text-sm text-gray-600 leading-relaxed">{item.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center">
              <div className="flex justify-center mb-3">
                <MessageSquare className="w-5 h-5 text-gray-600" />
              </div>
              <p className="text-sm text-gray-900 font-semibold">{t('help_cant_find', 'Can&apos;t find what you&apos;re looking for?')}</p>
              <button
                onClick={() => setActiveView('submit')}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-all duration-200"
              >
                <Plus className="w-4 h-4" />
                {t('help_submit_request_button', 'Submit a Request')}
              </button>
            </div>
          </motion.div>
        )}

        {/* Submit Request Form */}
        {activeView === 'submit' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category Selection */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8">
                <h3 className="text-md font-bold tracking-tight text-gray-900 mb-6 flex items-center gap-2">
                  <span className="text-2xl">📋</span>
                  {t('help_what_help', 'What do you need help with?')} <span className="text-red-500">*</span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {CATEGORIES.map(cat => {
                    const Icon = cat.icon;
                    const isSelected = form.category === cat.value;
                    return (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setForm({ ...form, category: cat.value })}
                        className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                          isSelected
                            ? `${cat.bg} ${cat.border} ring-2 ring-blue-500/20`
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${cat.color} mb-2`} />
                        <p className="text-sm font-semibold text-gray-900">{cat.label}</p>
                        <p className="text-[10px] text-gray-500 mt-0.5">{cat.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Request Details */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 space-y-4">
                <h3 className="text-md font-bold tracking-tight text-gray-900 flex items-center gap-2">
                  <span className="text-2xl">📝</span>
                  {t('help_request_details', 'Request Details')}
                </h3>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">{t('help_subject', 'Subject')} <span className="text-red-500">*</span></label>
                  <input
                    id="subject"
                    type="text"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder={t('help_subject_placeholder', 'Brief summary of your issue or request')}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300 outline-none transition-all"
                    maxLength={200}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">{t('help_message', 'Message')} <span className="text-red-500">*</span></label>
                  <textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={t('help_message_placeholder', 'Describe your issue in detail. Include steps to reproduce if reporting a bug.')}
                    rows={5}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300 outline-none transition-all resize-none"
                  />
                </div>

                <div>
                  <label htmlFor="priority" className="block text-sm font-semibold text-gray-700 mb-2">{t('help_priority', 'Priority')}</label>
                  <div className="flex gap-2" id="priority">
                    {PRIORITIES.map(p => (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => setForm({ ...form, priority: p.value })}
                        className={`px-4 py-2 rounded-lg text-xs font-semibold border transition-all duration-200 ${
                          form.priority === p.value
                            ? `${p.color} border-current ring-2 ring-offset-1`
                            : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-300 hover:bg-emerald-50'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 space-y-4">
                <h3 className="text-md font-bold tracking-tight text-gray-900 flex items-center gap-2">
                  <span className="text-2xl">👤</span>
                  {t('help_contact_info', 'Contact Information')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">{t('help_your_name', 'Your Name')} <span className="text-red-500">*</span></label>
                    <input
                      id="name"
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t('help_full_name_placeholder', 'Full name')}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">{t('help_email', 'Email')} <span className="text-red-500">*</span></label>
                    <input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder={t('help_email_placeholder', 'your@email.com')}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">{t('help_phone', 'Phone')} <span className="text-gray-400 font-normal">({t('help_optional', 'optional')})</span></label>
                    <input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder={t('help_phone_placeholder', '+1 (555) 000-0000')}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="preferredContact" className="block text-sm font-semibold text-gray-700 mb-2">{t('help_preferred_contact', 'Preferred Contact Method')}</label>
                    <select
                      id="preferredContact"
                      value={form.preferredContact}
                      onChange={(e) => setForm({ ...form, preferredContact: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300 outline-none transition-all"
                    >
                      <option value="email">{t('help_email', 'Email')}</option>
                      <option value="phone">{t('help_phone', 'Phone')}</option>
                      <option value="in_app">{t('help_in_app', 'In-App')}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={createMutation.isPending || !form.category || !form.subject || !form.message}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {createMutation.isPending ? (
                    <><Loader className="w-4 h-4 animate-spin" />{t('help_submitting', 'Submitting...')}</>
                  ) : (
                    <><Send className="w-4 h-4" />{t('help_submit_request', 'Submit Request')}</>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setForm(emptyForm)}
                  className="px-4 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg text-sm font-semibold transition-colors duration-200"
                >
                  {t('help_reset_form', 'Reset Form')}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Request History */}
        {activeView === 'history' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-5"
          >
            {/* Status Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter('')}
                className={`px-4 py-2.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                  !statusFilter ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                }`}
              >
                {t('help_all_statuses', 'All')}
              </button>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => setStatusFilter(key)}
                  className={`px-4 py-2.5 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center gap-1.5 ${
                    statusFilter === key ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <config.icon className="w-3.5 h-3.5" />
                  {config.label}
                </button>
              ))}
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-gray-900 font-semibold">{t('help_no_requests', 'No support requests yet')}</p>
                <p className="text-sm text-gray-600 mt-1">{t('help_no_requests_desc', 'Submit a request and track it here')}</p>
                <button
                  onClick={() => setActiveView('submit')}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-all duration-200"
                >
                  <Plus className="w-4 h-4" />
                  {t('help_submit_request_button', 'Submit Request')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map((req: any) => {
                  const isExpanded = expandedTicket === req.id;
                  const cat = CATEGORIES.find(c => c.value === req.category);
                  const CatIcon = cat?.icon || MessageSquare;
                  const status = STATUS_CONFIG[req.status] || STATUS_CONFIG.open;
                  const StatusIcon = status.icon;
                  const priority = PRIORITIES.find(p => p.value === req.priority);

                  return (
                    <motion.div
                      key={req.id}
                      layout
                      className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 shadow-sm"
                    >
                      {/* Header */}
                      <button
                        onClick={() => setExpandedTicket(isExpanded ? null : req.id)}
                        className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50 transition-colors duration-200"
                      >
                        <div className={`p-2.5 rounded-lg flex-shrink-0 ${cat?.bg || 'bg-gray-50'}`}>
                          <CatIcon className={`w-5 h-5 ${cat?.color || 'text-gray-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-sm font-bold text-gray-900 truncate">{req.subject}</h4>
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold flex items-center gap-1.5 flex-shrink-0 ${status.bg} ${status.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {status.label}
                            </span>
                            {priority && (
                              <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold flex-shrink-0 ${priority.color}`}>
                                {priority.label}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {cat?.label} &middot; {new Date(req.createdAt).toLocaleDateString()} at {new Date(req.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-blue-600 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                      </button>

                      {/* Expanded Details */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
                              {/* Message */}
                              <div className="bg-white rounded-xl p-4 border border-gray-200">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('help_your_message', 'Your Message')}</p>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{req.message}</p>
                              </div>

                              {/* Contact Info */}
                              <div className="flex flex-wrap gap-4 text-xs text-gray-600 bg-gray-50 rounded-xl p-4 border border-gray-200">
                                <span className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-gray-400" /><span className="font-medium">{req.email}</span></span>
                                {req.phone && <span className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-gray-400" /><span className="font-medium">{req.phone}</span></span>}
                                <span className="flex items-center gap-1.5"><User className="w-4 h-4 text-gray-400" /><span className="font-medium">{req.name}</span></span>
                              </div>

                              {/* Admin Notes */}
                              {req.adminNotes && (
                                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                                  <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1.5">
                                    <CheckCircle className="w-4 h-4" />
                                    {t('help_admin_response', 'Admin Response')}
                                  </p>
                                  <p className="text-sm text-green-800">{req.adminNotes}</p>
                                </div>
                              )}

                              {/* Follow-up Input */}
                              {req.status !== 'closed' && req.status !== 'resolved' && (
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={expandedTicket === req.id ? followUpText : ''}
                                    onChange={(e) => setFollowUpText(e.target.value)}
                                    placeholder={t('help_followup_placeholder', 'Add a follow-up message...')}
                                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 outline-none transition-all"
                                  />
                                  <button
                                    onClick={() => {
                                      if (followUpText.trim()) {
                                        updateMutation.mutate({ id: req.id, data: { message: followUpText } });
                                      }
                                    }}
                                    disabled={!followUpText.trim() || updateMutation.isPending}
                                    className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                  >
                                    <Send className="w-4 h-4" />
                                  </button>
                                </div>
                              )}

                              {/* Actions */}
                              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                                {req.status === 'open' && (
                                  <button
                                    onClick={() => updateMutation.mutate({ id: req.id, data: { status: 'closed' } })}
                                    className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors duration-200"
                                  >
                                    {t('help_close_request', 'Close Request')}
                                  </button>
                                )}
                                {req.status === 'resolved' && (
                                  <button
                                    onClick={() => updateMutation.mutate({ id: req.id, data: { status: 'open' } })}
                                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-200 transition-colors duration-200"
                                  >
                                    {t('help_reopen', 'Reopen')}
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    if (window.confirm('Delete this request?')) {
                                      deleteMutation.mutate(req.id);
                                    }
                                  }}
                                  className="px-2 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-xs ml-auto font-semibold transition-colors duration-200"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
